import { motion } from "framer-motion";

/* ---------- Diwali: marigold toran + glowing diyas ---------- */

function Diya({ className = "", delay = 0 }) {
  return (
    <motion.svg
      viewBox="0 0 48 40"
      className={className}
      animate={{ y: [0, -3, 0] }}
      transition={{ duration: 3.2, repeat: Infinity, delay, ease: "easeInOut" }}
      aria-hidden="true"
    >
      {/* flame glow */}
      <circle cx="24" cy="12" r="11" fill="#E8B44A" opacity="0.25" />
      {/* flame */}
      <motion.path
        d="M24 4c3 4.5 4.5 7 4.5 9.5a4.5 4.5 0 1 1-9 0C19.5 11 21 8.5 24 4z"
        fill="#F5C453"
        animate={{ scale: [1, 1.15, 1], opacity: [0.9, 1, 0.9] }}
        transition={{ duration: 1.6, repeat: Infinity, delay, ease: "easeInOut" }}
        style={{ transformOrigin: "24px 14px" }}
      />
      <path d="M24 8c1.6 2.6 2.4 4 2.4 5.6a2.4 2.4 0 1 1-4.8 0C21.6 12 22.4 10.6 24 8z" fill="#FFF3D6" />
      {/* bowl */}
      <path d="M8 22h32c0 6.5-6.5 12-16 12S8 28.5 8 22z" fill="#B96A28" />
      <path d="M8 22h32c0 2-1 3.6-2.5 5h-27C9 25.6 8 24 8 22z" fill="#D97B2B" />
      <ellipse cx="24" cy="22" rx="16" ry="3" fill="#8A4A1A" />
    </motion.svg>
  );
}

function Marigold({ className = "", color = "#E8912D" }) {
  return (
    <svg viewBox="0 0 24 24" className={className} aria-hidden="true">
      {[0, 45, 90, 135, 180, 225, 270, 315].map((r) => (
        <ellipse key={r} cx="12" cy="7" rx="3.2" ry="5" fill={color} transform={`rotate(${r} 12 12)`} />
      ))}
      <circle cx="12" cy="12" r="3.4" fill="#C25E10" />
    </svg>
  );
}

function ToranLeaf({ className = "" }) {
  return (
    <svg viewBox="0 0 16 24" className={className} aria-hidden="true">
      <path d="M8 0c4 6 5 12 0 24C3 12 4 6 8 0z" fill="#3E6B4F" />
      <path d="M8 2v20" stroke="#2C523B" strokeWidth="1" />
    </svg>
  );
}

function DiwaliFrame() {
  const garland = Array.from({ length: 15 });
  return (
    <>
      {/* marigold + leaf toran across the top */}
      <div className="absolute top-0 left-0 right-0 flex justify-between px-4 md:px-10 pointer-events-none z-10" aria-hidden="true">
        {garland.map((_, i) => (
          <div key={i} className="flex flex-col items-center" style={{ marginTop: i % 2 === 0 ? 0 : 6 }}>
            <div className="w-px h-5 bg-[#8EA696]/50" />
            {i % 3 === 1 ? (
              <ToranLeaf className="w-4 h-6" />
            ) : (
              <Marigold className="w-5 h-5" color={i % 4 === 0 ? "#E8B44A" : "#E8912D"} />
            )}
          </div>
        ))}
      </div>
      {/* glowing diyas, bottom corners */}
      <Diya className="absolute bottom-6 left-6 md:left-12 w-14 md:w-20 opacity-90 pointer-events-none z-10" delay={0.2} />
      <Diya className="absolute bottom-6 right-6 md:right-12 w-14 md:w-20 opacity-90 pointer-events-none z-10" delay={1.1} />
      {/* warm edge glow */}
      <div className="absolute inset-0 pointer-events-none" style={{ boxShadow: "inset 0 0 120px rgba(232,180,74,0.12)" }} aria-hidden="true" />
    </>
  );
}

/* ---------- New Year: floating gold sparkles ---------- */

function Sparkle({ x, y, size, delay }) {
  return (
    <motion.svg
      viewBox="0 0 24 24"
      className="absolute pointer-events-none"
      style={{ left: x, top: y, width: size, height: size }}
      animate={{ opacity: [0.15, 1, 0.15], scale: [0.7, 1.1, 0.7], rotate: [0, 20, 0] }}
      transition={{ duration: 3.4, repeat: Infinity, delay, ease: "easeInOut" }}
      aria-hidden="true"
    >
      <path d="M12 0l2.4 9.6L24 12l-9.6 2.4L12 24l-2.4-9.6L0 12l9.6-2.4L12 0z" fill="#E8C87A" />
    </motion.svg>
  );
}

const SPARKS = [
  { x: "6%", y: "14%", size: 18, delay: 0 },
  { x: "14%", y: "70%", size: 12, delay: 0.8 },
  { x: "26%", y: "8%", size: 10, delay: 1.6 },
  { x: "38%", y: "82%", size: 14, delay: 0.4 },
  { x: "52%", y: "6%", size: 16, delay: 2.1 },
  { x: "66%", y: "76%", size: 10, delay: 1.2 },
  { x: "78%", y: "12%", size: 18, delay: 0.6 },
  { x: "90%", y: "64%", size: 12, delay: 1.9 },
  { x: "94%", y: "22%", size: 9, delay: 2.6 },
  { x: "46%", y: "18%", size: 8, delay: 1.4 },
];

function NewYearFrame() {
  return (
    <>
      {SPARKS.map((s, i) => (
        <Sparkle key={i} {...s} />
      ))}
      {/* champagne double hairline inset */}
      <div className="absolute inset-3 md:inset-5 border border-[#E8C87A]/25 pointer-events-none" aria-hidden="true" />
      <div className="absolute inset-4 md:inset-6 border border-[#E8C87A]/10 pointer-events-none" aria-hidden="true" />
    </>
  );
}
/* ---------- Holi: soft floating gulal powder puffs ---------- */

const GULAL = [
  { x: "8%", y: "20%", size: 46, color: "#D9779B", delay: 0 },
  { x: "20%", y: "74%", size: 34, color: "#E8B44A", delay: 0.9 },
  { x: "33%", y: "10%", size: 26, color: "#7BA889", delay: 1.7 },
  { x: "55%", y: "80%", size: 40, color: "#D97B2B", delay: 0.5 },
  { x: "72%", y: "14%", size: 30, color: "#D9779B", delay: 1.3 },
  { x: "88%", y: "66%", size: 44, color: "#E8B44A", delay: 2.1 },
  { x: "94%", y: "24%", size: 24, color: "#7BA889", delay: 0.7 },
  { x: "45%", y: "26%", size: 22, color: "#D97B2B", delay: 2.6 },
];

function HoliFrame() {
  return (
    <>
      {GULAL.map((g, i) => (
        <motion.div
          key={i}
          className="absolute rounded-full pointer-events-none"
          style={{
            left: g.x,
            top: g.y,
            width: g.size,
            height: g.size,
            background: g.color,
            filter: "blur(14px)",
          }}
          animate={{ y: [0, -14, 0], opacity: [0.16, 0.3, 0.16], scale: [1, 1.12, 1] }}
          transition={{ duration: 5.5, repeat: Infinity, delay: g.delay, ease: "easeInOut" }}
          aria-hidden="true"
        />
      ))}
    </>
  );
}

/* ---------- Anniversary: gold confetti + ribbon curls ---------- */

const CONFETTI = [
  { x: "10%", delay: 0, dur: 7 },
  { x: "24%", delay: 1.4, dur: 8.5 },
  { x: "38%", delay: 0.6, dur: 7.6 },
  { x: "52%", delay: 2.2, dur: 9 },
  { x: "66%", delay: 1.0, dur: 7.2 },
  { x: "80%", delay: 1.9, dur: 8.2 },
  { x: "92%", delay: 0.3, dur: 7.8 },
];

function RibbonCurl({ className = "" }) {
  return (
    <svg viewBox="0 0 60 90" className={className} aria-hidden="true">
      <path
        d="M30 0c-14 12 10 18 2 30s-16 16-6 26 4 16-6 22"
        fill="none"
        stroke="#E8C87A"
        strokeWidth="2.5"
        strokeLinecap="round"
      />
      <path d="M30 0c-14 12 10 18 2 30" fill="none" stroke="#B9975B" strokeWidth="2.5" strokeLinecap="round" />
    </svg>
  );
}

function AnniversaryFrame() {
  return (
    <>
      {CONFETTI.map((c, i) => (
        <motion.div
          key={i}
          className="absolute pointer-events-none"
          style={{ left: c.x, top: -12 }}
          animate={{ y: ["-5vh", "105vh"], rotate: [0, 360], opacity: [0, 0.85, 0.85, 0] }}
          transition={{ duration: c.dur, repeat: Infinity, delay: c.delay, ease: "linear" }}
          aria-hidden="true"
        >
          <div className="w-1.5 h-3 rounded-[2px]" style={{ background: i % 2 ? "#E8C87A" : "#8EA696" }} />
        </motion.div>
      ))}
      <RibbonCurl className="absolute top-2 left-8 md:left-16 w-10 md:w-14 opacity-70 pointer-events-none" />
      <RibbonCurl className="absolute top-2 right-8 md:right-16 w-10 md:w-14 opacity-70 pointer-events-none -scale-x-100" />
    </>
  );
}



/* ---------- Custom: uploaded frame image draped over the top ---------- */

function CustomFrame({ cover }) {
  if (!cover.custom_image) return null;
  return (
    <div className="absolute top-0 left-0 right-0 flex justify-center pointer-events-none z-10" aria-hidden="true">
      <img src={cover.custom_image} alt="" className="max-h-24 md:max-h-32 w-auto object-contain" data-testid="spotlight-custom-frame-image" />
    </div>
  );
}

export function SpotlightCoverLayer({ cover }) {
  if (!cover || !cover.active || cover.active === "none") return null;
  return (
    <div data-testid="spotlight-cover-layer">
      {cover.active === "diwali" && <DiwaliFrame />}
      {cover.active === "newyear" && <NewYearFrame />}
      {cover.active === "holi" && <HoliFrame />}
      {cover.active === "anniversary" && <AnniversaryFrame />}
      {cover.active === "custom" && <CustomFrame cover={cover} />}
    </div>
  );
}

export function SpotlightCoverGreeting({ cover }) {
  if (!cover || !cover.active || cover.active === "none" || !cover.greeting) return null;
  return (
    <motion.p
      initial={{ opacity: 0, y: 10 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.8 }}
      className="font-display italic font-light text-2xl md:text-3xl text-[#E8C87A] mb-6"
      data-testid="spotlight-cover-greeting"
    >
      {cover.greeting}
    </motion.p>
  );
}
